import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { User } from '../types';
import { auth, db } from '../lib/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where, updateDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useActivityTracker } from './useActivityTracker';
import { useMultiTabSync } from './useMultiTabSync';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string, company: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  resetInactivityTimer: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 2 saat = 7200000 milisaniye
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;
// Uyarı süresi: Logout'tan 5 dakika önce (115 dakika)
const WARNING_TIMEOUT = (2 * 60 - 5) * 60 * 1000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isTabHiddenRef = useRef(false);
  const pausedAtRef = useRef<number | null>(null);
  const remainingTimeRef = useRef<number>(INACTIVITY_TIMEOUT);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Hareketsizlik zamanlayıcısını sıfırla
  const resetInactivityTimer = useCallback(() => {
    logger.activity('Resetting inactivity timer', {
      user: user?.username,
      remainingTime: remainingTimeRef.current
    });

    // Mevcut zamanlayıcıları temizle
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Uyarıyı kapat
    setShowInactivityWarning(false);

    if (!user) {
      logger.activity('No user logged in, skipping timer reset');
      return;
    }

    // Tab gizli değilse zamanlayıcıları başlat
    if (!isTabHiddenRef.current) {
      // Uyarı zamanlayıcısı (115 dakika sonra)
      warningTimerRef.current = setTimeout(() => {
        logger.warn('Inactivity warning triggered', {
          user: user.username,
          timeUntilLogout: '5 minutes'
        });
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);

      // Logout zamanlayıcısı (120 dakika sonra)
      inactivityTimerRef.current = setTimeout(() => {
        logger.auth('User inactive for 2 hours, logging out', {
          user: user.username
        });
        logout();
      }, INACTIVITY_TIMEOUT);

      remainingTimeRef.current = INACTIVITY_TIMEOUT;
      pausedAtRef.current = null;
    } else {
      logger.activity('Tab hidden, timer paused');
    }

    // Son aktivite zamanını kaydet
    localStorage.setItem('last_activity', Date.now().toString());
  }, [user]);

  // Multi-tab senkronizasyonu
  const { broadcastActivity, broadcastLogout } = useMultiTabSync(
    // onLogout
    () => {
      logger.auth('Logout broadcast received from another tab');
      performLogout(false); // Broadcast etme, zaten başka tab etti
    },
    // onActivity
    () => {
      logger.activity('Activity broadcast received from another tab');
      resetInactivityTimer();
    }
  );

  // Aktivite takibi
  useActivityTracker({
    onActivity: () => {
      resetInactivityTimer();
      broadcastActivity();
    },
    throttleMs: 2000 // 2 saniyede bir kontrol et
  });

  // Tab görünürlük değişikliklerini izle
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isHidden = document.hidden;
      isTabHiddenRef.current = isHidden;

      if (isHidden) {
        // Tab gizlendi - zamanlayıcıyı duraklat
        logger.session('Tab hidden, pausing inactivity timer');
        
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
        
        pausedAtRef.current = Date.now();
      } else {
        // Tab görünür oldu - zamanlayıcıyı devam ettir
        logger.session('Tab visible, resuming inactivity timer');
        
        if (pausedAtRef.current && user) {
          const pausedDuration = Date.now() - pausedAtRef.current;
          remainingTimeRef.current = Math.max(0, remainingTimeRef.current - pausedDuration);
          
          logger.activity('Timer resumed', {
            pausedDuration,
            remainingTime: remainingTimeRef.current
          });

          // Kalan süre çok azsa direkt logout
          if (remainingTimeRef.current < 60000) { // 1 dakikadan az
            logger.auth('Remaining time too short, logging out');
            logout();
            return;
          }

          // Zamanlayıcıyı kalan süre ile yeniden başlat
          const warningTime = remainingTimeRef.current - (5 * 60 * 1000);
          
          if (warningTime > 0) {
            warningTimerRef.current = setTimeout(() => {
              setShowInactivityWarning(true);
            }, warningTime);
          } else {
            setShowInactivityWarning(true);
          }

          inactivityTimerRef.current = setTimeout(() => {
            logout();
          }, remainingTimeRef.current);
        } else {
          // İlk kez görünür oldu, normal zamanlayıcıyı başlat
          resetInactivityTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetInactivityTimer]);

  // Auth kontrolü - sadece kullanıcı durumuna göre yönlendirme
  useEffect(() => {
    const checkAuth = () => {
      const savedUser = localStorage.getItem('crm_user');
      
      if (!savedUser && location.pathname !== '/login') {
        logger.auth('No user found, redirecting to login', {
          from: location.pathname
        });
        navigate('/login');
      } else if (savedUser && location.pathname === '/login') {
        logger.auth('User already logged in, redirecting to dashboard', {
          user: JSON.parse(savedUser).username
        });
        navigate('/proposal-overview');
      }
    };

    if (!isLoading) {
      checkAuth();
    }
  }, [location.pathname, navigate, isLoading]);

  // İlk yükleme - Firebase Auth oturum kontrolü
  // Firebase oturumu (request.auth) Firestore kuralları için zorunludur; bu yüzden
  // oturum durumu doğrudan Firebase Auth'tan okunur. Profil bilgisi localStorage'da
  // önbelleğe alınır (hızlı geri yükleme + hareketsizlik takibi için).
  useEffect(() => {
    logger.session('AuthProvider initializing (Firebase Auth)');

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const savedUser = localStorage.getItem('crm_user');
        const lastActivity = localStorage.getItem('last_activity');
        const timeSinceLastActivity = lastActivity ? Date.now() - parseInt(lastActivity) : 0;

        // Son aktiviteden 2 saat geçtiyse otomatik çıkış
        if (lastActivity && timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          logger.auth('Session expired due to inactivity', { timeSinceLastActivity });
          signOut(auth).catch(() => {});
          localStorage.removeItem('crm_user');
          localStorage.removeItem('last_activity');
          setUser(null);
          setIsLoading(false);
          return;
        }

        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            setUser(userData);
            remainingTimeRef.current = INACTIVITY_TIMEOUT - timeSinceLastActivity;
            logger.auth('Session restored', { user: userData.username, timeSinceLastActivity });
          } catch (error) {
            logger.error('Error parsing saved user', error);
            localStorage.removeItem('crm_user');
          }
        } else {
          // Firebase oturumu var ama profil önbelleği yok — minimal profil kur
          setUser({
            id: fbUser.uid,
            username: fbUser.email || '',
            email: fbUser.email || '',
            role: 'user',
            status: 'active',
          } as any);
        }
      } else {
        logger.session('No active Firebase session');
        setUser(null);
        localStorage.removeItem('crm_user');
        localStorage.removeItem('last_activity');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Firebase Auth ile giriş. İlk parametre e-posta adresidir (giriş ekranı e-posta gönderir).
  const login = async (emailOrUsername: string, password: string, _company?: string): Promise<boolean> => {
    const email = (emailOrUsername || '').trim();
    logger.auth('Login attempt', { email });

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      // Profil dokümanını getir (doc id = uid; yoksa e-posta ile ara)
      let profile: any = null;
      const byId = await getDoc(doc(db, 'users', uid));
      if (byId.exists()) {
        profile = { id: byId.id, ...(byId.data() as any) };
      } else {
        const snap = await getDocs(
          query(collection(db, 'users'), where('email', '==', email))
        );
        if (!snap.empty) {
          profile = { id: snap.docs[0].id, ...(snap.docs[0].data() as any) };
        }
      }

      if (!profile) {
        // Auth var ama profil yok — minimal profil
        profile = {
          id: uid,
          username: cred.user.email,
          email: cred.user.email,
          role: 'user',
          is_active: true,
        };
      }

      const isActive = profile.is_active ?? (profile.status === 'active');
      const userData: User = {
        id: profile.id,
        username: profile.username,
        email: profile.email,
        role: profile.role,
        company: profile.company,
        firstName: profile.first_name ?? profile.firstName,
        lastName: profile.last_name ?? profile.lastName,
        phone: profile.phone,
        status: isActive ? 'active' : 'inactive',
        createdAt: profile.created_at ?? profile.createdAt,
      } as User;

      // Son giriş zamanını güncelle (best-effort)
      try {
        await updateDoc(doc(db, 'users', profile.id), { last_login: new Date().toISOString() });
      } catch (e) {
        logger.warn('last_login update failed', e);
      }

      setUser(userData);
      localStorage.setItem('crm_user', JSON.stringify(userData));
      localStorage.setItem('last_activity', Date.now().toString());

      logger.auth('Firebase login successful', { user: userData.username, role: userData.role });
      return true;
    } catch (error) {
      logger.error('Firebase login error', error);
      return false;
    }
  };

  // Sifre sifirlama e-postasi gonderir (Firebase Auth).
  const resetPassword = async (emailInput: string): Promise<void> => {
    const email = (emailInput || '').trim();
    logger.auth('Password reset requested', { email });

    try {
      await sendPasswordResetEmail(auth, email);
      logger.auth('Password reset email sent', { email });
    } catch (error: any) {
      // Hesabin var olup olmadigini sizdirmamak icin bunu sessizce yutuyoruz.
      if (error?.code === 'auth/user-not-found') {
        logger.warn('Password reset requested for unknown account', { email });
        return;
      }
      logger.error('Password reset error', error);
      throw error;
    }
  };

  const performLogout = useCallback((shouldBroadcast: boolean = true) => {
    logger.auth('Performing logout', {
      user: user?.username,
      broadcast: shouldBroadcast
    });

    // Zamanlayıcıları temizle
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Firebase oturumunu kapat
    signOut(auth).catch((e) => logger.warn('signOut failed', e));

    // Kullanıcı bilgilerini temizle
    setUser(null);
    localStorage.removeItem('crm_user');
    localStorage.removeItem('last_activity');

    // NOT: Draft'ları temizleme - kullanıcı geri geldiğinde kurtarabilsin

    // Diğer tab'lere bildir
    if (shouldBroadcast) {
      broadcastLogout();
    }

    navigate('/login');
    logger.auth('User logged out');
  }, [user, navigate, broadcastLogout]);

  const logout = useCallback(() => {
    performLogout(true);
  }, [performLogout]);

  // Uyarı modalı için devam et fonksiyonu
  const handleContinueSession = useCallback(() => {
    logger.activity('User chose to continue session');
    setShowInactivityWarning(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  return (
    <AuthContext.Provider value={{ user, login, resetPassword, logout, isLoading, resetInactivityTimer }}>
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      ) : (
        children
      )}

      {/* Hareketsizlik Uyarı Modalı */}
      {showInactivityWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-time-line text-amber-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Oturum Sona Eriyor</h3>
                <p className="text-sm text-gray-600">5 dakika içinde otomatik çıkış yapılacak</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Uzun süredir hareketsizsiniz. Çalışmaya devam etmek için lütfen butona tıklayın.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleContinueSession}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                Devam Et
              </button>
              
              <button
                onClick={logout}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
